import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useImmer } from "use-immer";
import { flatMap, groupBy, keys, map, sortBy, uniq } from "lodash";
import { useToast } from "../ui/Toast";
import { Hint } from "../ui/Hint";

import {
  createShoppingList,
  deleteAvailability,
  getShoppingListRequirements,
  saveAvailabilities,
  updateAvailabilities,
} from "../../api";
import { ShoppingListRequirementDto } from "../../api";
import { ShoppingListCreationDto } from "../../api";
import Food from "../../data/food";
import { queryKeys, usePersonsAsync, useShops } from "../../queries/queries";
import {
  useAvailabilitiesByPurchases,
  usePurchasesByIds,
} from "../../queries/caches";
import { useQueryClient } from "@tanstack/react-query";
import {
  ensurePurchases,
  getPurchaseById,
  invalidateAvailabilities,
} from "../../queries/caches";
import { getDefaultPurchaseUnit } from "../../utils/purchaseUtils";
import { formatIsoString, todayForPlan } from "../../utils/dateUtils";
import { DrawerHistoryContext } from "../../contexts";
import { usePlan2PlateDisclosure } from "../Plan2PlateDrawer";
import { useUnloadAlert } from "../useUnloadAlert";
import { SearchForFoodDrawer } from "../SearchForFoodDrawer";
import {
  AvailabilityDrawer,
  useAvailabilityDrawer,
} from "../AvailabilityDrawer";
import { isDeleted, isEdited, isNew } from "../../utils/editUtils";
import { MealIndToggleRow } from "./MealIndToggleRow";
import { RequirementRow } from "./RequirementRow";
import { ShopOption, ShopSelectorDropdown } from "./ShopSelectorDropdown";
import { Plan2PlateIcon } from "../Plan2PlateIcon";
import { cn } from "../../lib/utils";

const ShoppingListCreateDrawerContent = (props: { onCreated?: () => void }) => {
  const { setPristine } = useUnloadAlert();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [startDate, setStartDate] = useState(todayForPlan());
  const [endDate, setEndDate] = useState(() => {
    const d = todayForPlan();
    d.setDate(d.getDate() - 1);
    return d;
  });
  const [startMealInd, setStartMealInd] = useState<string>("F");
  const [endMealInd, setEndMealInd] = useState<string>("A");

  const updateStartDate = (date: Date) => {
    date.setHours(date.getHours() + 12, 0, 0);
    date.setUTCHours(0, 0, 0);
    setStartDate(date);
  };
  const updateEndDate = (date: Date) => {
    date.setHours(date.getHours() + 12, 0, 0);
    date.setUTCHours(0, 0, 0);
    setEndDate(date);
    setPristine(false);
  };

  const isValidRange = endDate.getTime() >= startDate.getTime();

  // --- data (Suspense-Query-Cache) ---
  const shops = useShops();
  const persons = usePersonsAsync(false);

  // --- state ---
  const [requirements, setRequirements] = useImmer<
    ShoppingListRequirementDto[]
  >([]);
  const [requirementsSorted, setRequirementsSorted] = useImmer<
    ShoppingListRequirementDto[]
  >([]);
  const [selectedShopIds, setSelectedShopIds] = useImmer<number[]>([]);
  const [selectedPersonIds, setSelectedPersonIds] = useImmer<number[]>([]);

  const purchaseUnitIds = useMemo(
    () => [
      ...new Set(
        requirements
          .map((requirement) => requirement.purchaseUnitId)
          .filter((id) => id > 0),
      ),
    ],
    [requirements],
  );
  const purchases = usePurchasesByIds([], [], purchaseUnitIds);

  const purchaseByDefaultUnitLookup = useMemo(() => {
    const result: Record<number, (typeof purchases)[number]> = {};
    purchases
      .filter((p) => p.purchaseRelevant)
      .forEach((purchase) => {
        result[getDefaultPurchaseUnit(purchase).id] = purchase;
      });
    return result;
  }, [purchases]);

  const purchaseIds = useMemo(
    () => [...new Set(purchases.map((purchase) => purchase.id))],
    [purchases],
  );
  const availabilities = useAvailabilitiesByPurchases(purchaseIds);

  const availabilitiesByPurchaseId = useMemo(() => {
    const map = new Map<number, typeof availabilities>();
    availabilities.forEach((a) => {
      const arr = map.get(a.purchaseId) ?? [];
      arr.push(a);
      map.set(a.purchaseId, arr);
    });
    return map;
  }, [availabilities]);

  // init person selection: all active persons selected
  useEffect(() => {
    setSelectedPersonIds(persons.map((p) => p.id));
  }, [persons]);

  // --- load requirements from backend ---
  const loadRequirements = useCallback(() => {
    if (!isValidRange) return;
    getShoppingListRequirements({
      query: {
        startDate: formatIsoString(startDate),
        endDate: formatIsoString(endDate),
        startMealInd,
        endMealInd,
        personIds: selectedPersonIds,
      },
    }).then((res) => {
      if (!res.data.length) {
        toast({
          title:
            "Keine einkaufsrelevanten Einträge für den gewählten Zeitraum gefunden",
          status: "info",
          isClosable: true,
        });
      }
      setRequirements(res.data);
    });
  }, [startDate, endDate, startMealInd, endMealInd, selectedPersonIds]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  useEffect(() => {
    if (!keys(purchaseByDefaultUnitLookup).length) return;
    setRequirementsSorted(
      sortBy(
        requirements,
        (r) => purchaseByDefaultUnitLookup[r.purchaseUnitId]?.description ?? "",
      ),
    );
  }, [requirements, purchaseByDefaultUnitLookup]);

  // --- availability / shop logic ---
  const relevantPurchaseIds = keys(purchaseByDefaultUnitLookup).length
    ? requirementsSorted
        .filter((r) => r.amount > 0)
        .map((r) => purchaseByDefaultUnitLookup[r.purchaseUnitId]?.id)
        .filter(Boolean)
    : [];

  const relevantAvailabilities = flatMap(
    relevantPurchaseIds,
    (purchaseId) => availabilitiesByPurchaseId.get(purchaseId!) || [],
  );
  const availabilityByShopId = groupBy(relevantAvailabilities, (a) => a.shopId);

  const purchaseableUnitIds = uniq(
    map(
      flatMap(selectedShopIds, (s) => availabilityByShopId[s] || []),
      (a) =>
        getDefaultPurchaseUnit(purchases.find((p) => p.id === a.purchaseId)!)
          .id,
    ),
  );

  const missingRequirements = requirementsSorted.filter(
    (r) => !purchaseableUnitIds.includes(r.purchaseUnitId) && r.amount > 0,
  );

  const shopOptions: ShopOption[] = shops.map((shop) => {
    const avails = availabilityByShopId[shop.id] || [];
    const availablePurchasableUnitIds = uniq(
      avails.map(
        (a) =>
          getDefaultPurchaseUnit(purchases.find((p) => p.id === a.purchaseId)!)
            .id,
      ),
    );
    const availableCount = requirementsSorted.filter((r) =>
      availablePurchasableUnitIds.includes(r.purchaseUnitId),
    ).length;
    return {
      id: shop.id,
      description: shop.description,
      availableCount,
      totalCount: relevantPurchaseIds.length,
    };
  });

  // --- submit ---
  const submitShoppingList = async () => {
    const request: ShoppingListCreationDto = {
      selectedShops: selectedShopIds,
      shoppingListRequirementDtos: requirementsSorted,
    };
    await createShoppingList({ body: request });
    props.onCreated?.();
  };

  // --- drawer: add extra item ---
  // nutzt den geteilten Drawer-History-Stack der App – ein separater Stack
  // würde beim Schließen nested Drawer fälschlich den Haupt-Drawer mit poppen
  const drawerHistoryCtx = useContext(DrawerHistoryContext);
  const {
    isOpen: isSearchOpen,
    onClose: onSearchClose,
    onOpen: onSearchOpen,
  } = usePlan2PlateDisclosure(drawerHistoryCtx);

  const alreadyListedPurchaseUnitIds = new Set(
    requirementsSorted.map((r) => r.purchaseUnitId),
  );

  const addRequirement = async (food: Food) => {
    if (!food) return;
    await ensurePurchases(queryClient, [food.defaultPurchaseId]);
    const purchase = getPurchaseById(queryClient, food.defaultPurchaseId);
    if (!purchase?.purchaseRelevant) {
      toast({
        title: "Dieses Lebensmittel ist nicht einkaufsrelevant",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    const defaultUnit = getDefaultPurchaseUnit(purchase);
    if (alreadyListedPurchaseUnitIds.has(defaultUnit.id)) {
      toast({
        title: "Dieses Lebensmittel steht bereits auf der Einkaufsliste",
        status: "info",
        isClosable: true,
      });
      return;
    }
    const requirement: ShoppingListRequirementDto = {
      amount: 0,
      amountInStock: 0,
      purchaseUnitId: defaultUnit.id,
      mealDetails: [],
    };
    setRequirements((draft) => {
      draft.push(requirement);
    });
  };

  // --- drawer: availability ---
  const availabilityDrawerProps = useAvailabilityDrawer(async (avails) => {
    const created = avails.filter(isNew);
    const edited = avails.filter(isEdited);
    const deleted = avails.filter(isDeleted);
    await saveAvailabilities({ body: created });
    await updateAvailabilities({ body: edited });
    await Promise.all(
      deleted.map((a) =>
        deleteAvailability({ path: { availabilityId: String(a.id) } }),
      ),
    );
    await invalidateAvailabilities(queryClient);
    return true;
  }, true);

  const openAvailabilityFor = (purchaseUnitId: number) => {
    const purchase = purchaseByDefaultUnitLookup[purchaseUnitId];
    if (!purchase) return;
    const avails = (availabilitiesByPurchaseId.get(purchase.id) || []).map(
      (a) => ({ ...a, edited: false, deleted: false }),
    );
    availabilityDrawerProps.editAvailabilities(purchase.id, avails);
  };

  const activePersons = persons;

  return (
    <>
      <SearchForFoodDrawer
        onClose={onSearchClose}
        open={isSearchOpen}
        selectFood={addRequirement}
      />
      <AvailabilityDrawer {...availabilityDrawerProps} />

      <div className="flex flex-col gap-4">
        {/* Datumsbereich */}
        <div className="flex flex-col gap-2">
          <MealIndToggleRow
            date={startDate}
            onDateChange={updateStartDate}
            selectedMealInd={startMealInd}
            onMealIndChange={setStartMealInd}
          />
          <MealIndToggleRow
            date={endDate}
            onDateChange={updateEndDate}
            selectedMealInd={endMealInd}
            onMealIndChange={setEndMealInd}
          />
        </div>

        {/* Personen-Filter */}
        {activePersons.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activePersons.map((person) => {
              const selected = selectedPersonIds.includes(person.id);
              return (
                <button
                  key={person.id}
                  type="button"
                  onClick={() => {
                    setSelectedPersonIds((draft) => {
                      const idx = draft.indexOf(person.id);
                      if (idx >= 0) {
                        draft.splice(idx, 1);
                      } else {
                        draft.push(person.id);
                      }
                    });
                  }}
                  className={cn(
                    "rounded-pill px-3 py-1 text-sm font-medium transition border",
                    selected
                      ? "bg-gold/10 border-gold text-gold"
                      : "bg-surface-card border-line text-ink-muted",
                  )}
                >
                  {person.name}
                </button>
              );
            })}
          </div>
        )}

        {isValidRange && (
          <>
            {/* Anforderungsliste */}
            <Hint className="rounded-card border border-line bg-surface p-3 text-xs text-ink-muted flex flex-col gap-1">
              <div className="flex items-start gap-1.5">
                <span className="font-bold text-lg leading-none text-protein">
                  ·
                </span>
                <span>
                  Keine Einkaufsmenge – der Artikel wird nicht gekauft. Trage
                  die benötigte Menge ein, z.&nbsp;B. bei manuell hinzugefügten
                  Artikeln.
                </span>
              </div>
              <div className="flex items-start gap-1.5">
                <span className="font-bold text-lg leading-none text-gold">
                  ·
                </span>
                <span>
                  Nicht in den gewählten Geschäften verfügbar – passe die
                  Geschäftsauswahl an oder setze die Verfügbarkeit des Artikels
                  über das Einkaufswagen-Symbol.
                </span>
              </div>
            </Hint>
            <div className="rounded-card border border-line bg-surface-card px-3">
              {requirementsSorted.map((req, index) => {
                const purchase =
                  purchaseByDefaultUnitLookup[req.purchaseUnitId];
                if (!purchase) return null;
                return (
                  <RequirementRow
                    key={getDefaultPurchaseUnit(purchase).id}
                    requirement={req}
                    purchase={purchase}
                    missingInShops={missingRequirements.some(
                      (m) => m.purchaseUnitId === req.purchaseUnitId,
                    )}
                    onAmountChange={(amount) => {
                      setRequirementsSorted((draft) => {
                        draft[index].amount = amount;
                      });
                      setRequirements((draft) => {
                        const orig = draft.find(
                          (r) => r.purchaseUnitId === req.purchaseUnitId,
                        );
                        if (orig) orig.amount = amount;
                      });
                    }}
                    onDelete={() => {
                      setRequirementsSorted((draft) => {
                        draft.splice(index, 1);
                      });
                      setRequirements((draft) => {
                        const idx = draft.findIndex(
                          (r) => r.purchaseUnitId === req.purchaseUnitId,
                        );
                        if (idx >= 0) draft.splice(idx, 1);
                      });
                    }}
                    onAvailabilityOpen={() =>
                      openAvailabilityFor(req.purchaseUnitId)
                    }
                  />
                );
              })}

              {/* Hinzufügen */}
              <button
                type="button"
                onClick={onSearchOpen}
                className="flex w-full items-center justify-center gap-2 py-3 text-sm text-ink-muted hover:text-ink transition"
              >
                <Plan2PlateIcon icon="plus" size="sm" />
                Zur Einkaufsliste hinzufügen
              </button>
            </div>

            <div className="border-t border-line" />

            {/* Geschäft-Auswahl */}
            {!!requirements.length && (
              <ShopSelectorDropdown
                shops={shopOptions}
                selectedIds={selectedShopIds}
                onSelectionChange={(ids) => setSelectedShopIds(ids)}
              />
            )}

            {/* Fehlende Artikel */}
            {missingRequirements.length > 0 && selectedShopIds.length > 0 && (
              <div className="flex items-start gap-2 rounded-card border border-gold/40 bg-gold/10 px-4 py-3">
                <Plan2PlateIcon icon="warning" size="sm" />
                <p className="text-xs text-gold font-medium">
                  {missingRequirements
                    .map(
                      (r) =>
                        purchaseByDefaultUnitLookup[r.purchaseUnitId]
                          ?.description,
                    )
                    .filter(Boolean)
                    .join(", ")}{" "}
                  – in keinem der ausgewählten Geschäfte vorhanden
                </p>
              </div>
            )}

            {/* Einkaufen-Button */}
            <button
              type="button"
              disabled={selectedShopIds.length === 0}
              onClick={submitShoppingList}
              className="w-full rounded-card bg-teal py-3 text-sm font-bold text-white disabled:opacity-40 transition"
            >
              ✓ Einkaufen
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default ShoppingListCreateDrawerContent;
